import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  Box,
  Stack,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import { FaArrowLeft, FaEdit, FaTrash, FaImage } from "react-icons/fa";
import { MathfieldElement } from "mathlive";
import "katex/dist/katex.min.css";
import "mathlive";
import "../css/mathlive.core.css";

import { API_URL } from '../config';
import { BlockMath, InlineMath } from 'react-katex';

if (!customElements.get("math-field")) {
  customElements.define("math-field", MathfieldElement);
}

export default function AdminTestOptionPage() {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const imageInputRef = useRef(null);

  const [mode, setMode] = useState("text");
  const [options, setOptions] = useState([]);
  const [optionLetter, setOptionLetter] = useState("");
  const [isAnswer, setIsAnswer] = useState(false);
  const [optionText, setOptionText] = useState("");
  const [imageUri, setImageUri] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [isRemovingBg, setIsRemovingBg] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOptions = useCallback(() => {
    setLoading(true);
    fetch(`${API_URL}/admin/options/${questionId}/`)
      .then((res) => res.json())
      .then((data) => {
        setOptions(data);
        setLoading(false);
      })
      .catch(() => {
        alert("Failed to fetch options.");
        setLoading(false);
      });
  }, [questionId]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  const resetForm = () => {
    setOptionLetter("");
    setIsAnswer(false);
    setOptionText("");
    setEditingOption(null);
    setImageUri(null);
    setImagePreview(null);
    setExistingImage(null);
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const removeBackgroundWithRemoveBg = async (file) => {
    const formData = new FormData();
    formData.append("image_file", file);
    formData.append("size", "auto");

    const res = await fetch("https://api.remove.bg/v1.0/removebg", {
      method: "POST",
      headers: { "X-Api-Key": "hxRA6YNfZzrnsXgMYv9ZBYCh" },
      body: formData,
    });

    if (!res.ok) throw new Error("Background removal failed");
    return await res.blob();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsRemovingBg(true);
    try {
      const cleanedBlob = await removeBackgroundWithRemoveBg(file);
      const cleanedFile = new File([cleanedBlob], "cleaned.png", {
        type: "image/png",
      });
      setImageUri(cleanedFile);
      setImagePreview(URL.createObjectURL(cleanedFile));
      setExistingImage(null);
    } catch {
      alert("Background removal failed – using original image.");
      setImageUri(file);
      setImagePreview(URL.createObjectURL(file));
      setExistingImage(null);
    } finally {
      setIsRemovingBg(false);
    }
  };

  const handleSubmit = () => {
    if (!optionLetter || !optionText.trim()) {
      alert("Option Letter and Text are required.");
      return;
    }

    const formData = new FormData();
    formData.append("optionLetter", optionLetter);
    formData.append("text", optionText);
    formData.append("isAnswer", isAnswer);
    if (imageUri) formData.append("option_img", imageUri);

    const url = editingOption
      ? `${API_URL}/admin/option/${editingOption.id}/`
      : `${API_URL}/admin/options/${questionId}/`;
    const method = editingOption ? "PUT" : "POST";

    fetch(url, { method, body: formData })
      .then((res) => res.json())
      .then(() => {
        fetchOptions();
        resetForm();
      })
      .catch(() => alert("Error submitting option."));
  };

  const handleEdit = (option) => {
    setEditingOption(option);
    setOptionLetter(option.option_letter);
    setIsAnswer(option.is_answer);
    setOptionText(option.text);
    setExistingImage(option.option_img);
    setImageUri(null);
    setImagePreview(null);
    if (imageInputRef.current) imageInputRef.current.value = "";
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const confirmDeleteOption = (optionId) => {
    if (!window.confirm("Delete this option permanently?")) return;
    fetch(`${API_URL}/admin/option/${optionId}/`, { method: "DELETE" })
      .then(() =>
        setOptions((prev) => prev.filter((opt) => opt.id !== optionId))
      )
      .catch(() => alert("Failed to delete option."));
  };


  const renderMath = (text) => {
    if (!text) return null;

    const parts = text.split(
      /(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\\\([\s\S]+?\\\)|\$[^$]+\$)/g
    );

    return parts.map((part, index) => {
      // $$...$$ (block)
      if (part.startsWith("$$") && part.endsWith("$$")) {
        return (
          <div key={index} style={{ margin: "10px 0" }}>
            <BlockMath math={part.slice(2, -2)} />
          </div>
        );
      }

      // \[...\] (block)
      if (part.startsWith("\\[") && part.endsWith("\\]")) {
        return (
          <div key={index} style={{ margin: "10px 0" }}>
            <BlockMath math={part.slice(2, -2)} />
          </div>
        );
      }

      // \(...\) (inline)
      if (part.startsWith("\\(") && part.endsWith("\\)")) {
        return (
          <InlineMath key={index} math={part.slice(2, -2)} />
        );
      }

      // $...$ (inline)
      if (part.startsWith("$") && part.endsWith("$")) {
        return (
          <InlineMath key={index} math={part.slice(1, -1)} />
        );
      }

      // Normal text
      return (
        <span key={index} style={{ whiteSpace: "pre-line" }}>
          {part}
        </span>
      );
    });
  };

  
  return (
    <Box sx={{ maxWidth: 900, mx: "auto", p: { xs: 2, sm: 3 } }}>
      {/* Back Button */}
      <IconButton onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        <FaArrowLeft />
      </IconButton>

      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        align="center"
        fontWeight="600"
      >
        {editingOption ? "Edit Option" : "Add New Option"}
      </Typography>

      {/* Form Card */}
      <Card elevation={4} sx={{ mb: 4 }}>
        <CardContent sx={{ pb: 1 }}>
          <Stack spacing={3}>
            {/* Option Letter */}
            <FormControl fullWidth>
              <InputLabel>Select Option Letter</InputLabel>
              <Select
                value={optionLetter}
                label="Select Option Letter"
                onChange={(e) => setOptionLetter(e.target.value)}
              >
                <MenuItem value="">Select...</MenuItem>
                {["A", "B", "C", "D"].map((l) => (
                  <MenuItem key={l} value={l}>
                    Option {l}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Text / Math Input */}
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
              {mode === "text" ? (
                <textarea
                  placeholder="Option text"
                  value={optionText}
                  onChange={(e) => setOptionText(e.target.value)}
                  style={{
                    flex: 1,
                    minHeight: 120,
                    padding: "12px",
                    fontSize: "16px",
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                    resize: "vertical",
                  }}
                />
              ) : (
                <math-field
                  style={{
                    flex: 1,
                    minHeight: "120px",
                    padding: "12px",
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                    background: "#fff",
                  }}
                  value={optionText.replace(/^\$|\$$/g, "")} // remove $ for editing display
                  onInput={(e) => {
                    const latex = e.target.getValue();
                    setOptionText(`$${latex}$`); // wrap with $$
                  }}
                />
              )}

              <Stack direction="column" spacing={1}>
                <Button
                  variant={mode === "text" ? "contained" : "outlined"}
                  onClick={() => setMode("text")}
                >
                  Text
                </Button>
                <Button
                  variant={mode === "math" ? "contained" : "outlined"}
                  onClick={() => setMode("math")}
                >
                  Math
                </Button>
              </Stack>
            </Box>

            {/* Is Answer */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAnswer}
                  onChange={() => setIsAnswer(!isAnswer)}
                />
              }
              label="This is the correct answer"
            />

            {/* Image Upload */}
            <Box>
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="image-upload"
                type="file"
                ref={imageInputRef}
                onChange={handleImageUpload}
              />
              <label htmlFor="image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<FaImage />}
                >
                  Upload Image
                </Button>
              </label>

              {isRemovingBg && (
                <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    Removing background...
                  </Typography>
                </Box>
              )}

              {(imagePreview || existingImage) && (
                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <img
                    src={imagePreview || existingImage}
                    alt="Option preview"
                    style={{
                      maxHeight: 300,
                      maxWidth: "100%",
                      borderRadius: 8,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  />
                </Box>
              )}
            </Box>
          </Stack>
        </CardContent>

        <CardActions sx={{ justifyContent: "flex-end", p: 2, gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isRemovingBg}
          >
            {editingOption ? "Update Option" : "Add Option"}
          </Button>
          {editingOption && (
            <Button variant="outlined" color="inherit" onClick={resetForm}>
              Cancel
            </Button>
          )}
        </CardActions>
      </Card>

      <Divider sx={{ my: 4 }}>
        <Chip label="Existing Options" />
      </Divider>

      {/* Options List */}
      {loading ? (
        <Box textAlign="center" py={4}>
          <CircularProgress />
        </Box>
      ) : options.length === 0 ? (
        <Alert severity="info">No options added yet.</Alert>
      ) : (
        <Stack spacing={2}>
          {options.map((option) => (
            <Card key={option.id} variant="outlined">
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="h6" component="span">
                    Option {option.option_letter}
                  </Typography>
                  {option.is_answer && (
                    <Chip
                      label="Correct Answer"
                      color="success"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    {renderMath(option.text)}
                  </Typography>
                  {option.option_img && (
                    <Box sx={{ mt: 2 }}>
                      <img
                        src={option.option_img}
                        alt="Option"
                        style={{ maxHeight: 150, borderRadius: 6 }}
                      />
                    </Box>
                  )}
                </Box>

                <Stack direction="row" spacing={1}>
                  <Tooltip title="Edit">
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(option)}
                    >
                      <FaEdit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      color="error"
                      onClick={() => confirmDeleteOption(option.id)}
                    >
                      <FaTrash />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
}
